import { Link } from 'react-router-dom';
import StatusBadge from '@dashboard/shared/StatusBadge';
import { timeAgo } from '@dashboard/shared/timeAgo';
import type { Order, OrderStatus } from '@shared/types';

type OrderKind = 'stripe' | 'square' | 'crypto' | 'unknown';

function getOrderKind(o: Order): OrderKind {
  if (o.provider === 'stripe' || o.payment_provider === 'stripe') return 'stripe';
  if (o.provider === 'square' || o.payment_provider === 'square') return 'square';
  if (o.provider === 'crypto' || o.payment_provider === 'crypto') return 'crypto';
  return 'unknown';
}

interface KindMeta {
  label: string;
  basePath: string | null;
  glyph: string;
  className: string;
}

const KIND_META: Record<OrderKind, KindMeta> = {
  stripe: {
    label: 'Stripe',
    basePath: '/dashboard/stripe',
    glyph: '\u22A1',
    className: 'text-sky-300 bg-sky-500/10 border-sky-500/20',
  },
  square: {
    label: 'Square',
    basePath: '/dashboard/square',
    glyph: '\u25A0',
    className: 'text-amber-300 bg-amber-500/10 border-amber-500/20',
  },
  crypto: {
    label: 'Crypto',
    basePath: '/dashboard/crypto',
    glyph: '\u25C7',
    className: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20',
  },
  unknown: {
    label: 'Unknown',
    basePath: null,
    glyph: '?',
    className: 'text-zinc-400 bg-zinc-500/10 border-zinc-500/20',
  },
};

interface Props {
  orders: Order[];
  loading: boolean;
}

export default function RecentOrdersTable({ orders, loading }: Props) {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'var(--dash-text-muted)' }}>
          Recent Orders
        </h2>
      </div>

      {loading ? (
        <p className="font-sans text-sm" style={{ color: 'var(--dash-text-muted)' }}>Loading...</p>
      ) : orders.length === 0 ? (
        <div
          className="rounded p-8 text-center"
          style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
        >
          <p className="font-sans text-sm" style={{ color: 'var(--dash-text-muted)' }}>No orders yet.</p>
        </div>
      ) : (
        <div className="rounded overflow-hidden" style={{ border: '1px solid var(--dash-border)' }}>
          <table className="w-full text-sm font-sans">
            <thead>
              <tr style={{ background: 'var(--dash-card)', borderBottom: '1px solid var(--dash-border)' }}>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-muted)' }}>Order</th>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-muted)' }}>Solution</th>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-muted)' }}>Amount</th>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-muted)' }}>Status</th>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-muted)' }}>Time</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order, i) => {
                const kind = getOrderKind(order);
                const meta = KIND_META[kind];
                return (
                  <tr
                    key={order.id}
                    className="transition-colors"
                    style={{
                      background: i % 2 === 0 ? 'var(--dash-row-even)' : 'var(--dash-row-odd)',
                      borderBottom: '1px solid var(--dash-border-light)',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dash-hover-bg)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = i % 2 === 0 ? 'var(--dash-row-even)' : 'var(--dash-row-odd)'; }}
                  >
                    <td className="px-4 py-2.5">
                      {meta.basePath ? (
                        <Link to={`${meta.basePath}/orders/${order.id}`} className="font-mono text-xs transition-colors" style={{ color: 'var(--dash-text-tertiary)' }}>
                          {order.id.slice(0, 8)}...
                        </Link>
                      ) : (
                        <span className="font-mono text-xs" style={{ color: 'var(--dash-text-muted)' }}>
                          {order.id.slice(0, 8)}...
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${meta.className}`}>
                        <span className="text-[9px]">{meta.glyph}</span>
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs" style={{ color: 'var(--dash-text-tertiary)' }}>
                      ${order.expected_amount.toFixed(2)}
                    </td>
                    <td className="px-4 py-2.5">
                      <StatusBadge status={order.status as OrderStatus} />
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs" style={{ color: 'var(--dash-text-muted)' }}>
                      {timeAgo(order.created_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
